/**
 * CSV writer — G2.4: "CSV (UTF-8 with BOM for Excel, RFC 4180)".
 *
 * Both halves of that matter and both are easy to get wrong:
 *
 * The BOM is not decoration. Excel on Windows reads a BOM-less UTF-8 CSV in the
 * system code page, so an Italian store name with an accent, or a currency symbol,
 * arrives corrupted — and a planner who has seen one mojibake file stops trusting
 * the export path entirely.
 *
 * RFC 4180 means CRLF line endings, `"` doubled inside a quoted field, and quoting
 * whenever a field contains a comma, a quote or a line break. It does NOT mean
 * "join with commas", which is what most hand-rolled writers do.
 *
 * Values are RAW. G2.4: "Raw values, full precision, unrounded, ISO-8601 dates,
 * explicit currency column — never a display string." There is no locale formatting
 * in this file and there must never be: `toLocaleString` here would silently turn
 * 1234.5 into `1.234,5` for an Italian planner and then break every downstream
 * parser that reads the file.
 *
 * **The second dialect** (owner ruling 2026-10-22, `docs/adr/9072`): Excel set to Italian or
 * Spanish reads this file's decimal point as a thousands separator — `26.7379679144385` opened as
 * 26.737.967.914.438.500. So beside the raw CSV every export also offers «CSV for , decimal»:
 * `;` between fields and a decimal comma in every number, still full precision and unrounded
 * (`String(n)` with `.` → `,`), no thousands separators; integers, ISO dates, missing-state codes
 * and text exactly as in the raw file. It is NOT locale formatting: the same dialect is written
 * whatever the UI language. The raw CSV stays the file for systems and stays mandatory; this one
 * is for a person opening the file in Excel.
 */

import type { CsvDialect, ExportCell, ExportColumn, ExportRequest } from './types';
import { fileRow, headerFields } from './table';

export const UTF8_BOM = '﻿';
const CRLF = '\r\n';

/** Each dialect's field separator. */
export const CSV_SEPARATOR: Readonly<Record<CsvDialect, string>> = { standard: ',', 'decimal-comma': ';' };

const NEEDS_QUOTING: Readonly<Record<CsvDialect, RegExp>> = { standard: /[",\r\n]/, 'decimal-comma': /[";\r\n]/ };

/**
 * RFC 4180 §2.6/2.7, with the dialect's separator: the raw file quotes a field holding `,`, `"` or
 * a line break; the decimal-comma file one holding `;`, `"` or a line break (its commas are decimals
 * and text, never separators).
 */
export function escapeField(value: string, dialect: CsvDialect = 'standard'): string {
  if (NEEDS_QUOTING[dialect].test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

/** A decimal as the dialect writes it: the raw `String(n)`, its point a comma in the decimal-comma file. */
function decimal(raw: number, dialect: CsvDialect): string {
  const text = String(raw);
  return dialect === 'decimal-comma' ? text.replace('.', ',') : text;
}

/**
 * Formula injection (OWASP "CSV Injection"; DEC-100, ADR 9010 amendment of 2026-10-01).
 * A spreadsheet evaluates a field that starts with `=`, `+`, `-`, `@`, a tab or a carriage
 * return, so a store or article name such as `=HYPERLINK(...)` would run when the planner
 * opens the file. Free text is the only place such a value can come from: numbers, dates and
 * the missing-state codes are written by this file and never start that way, and a negative
 * number is a `number` cell, so it is never touched. The defence is the standard one, a
 * leading apostrophe, which Excel and LibreOffice show as text and do not evaluate. It is
 * the one departure from "values are RAW"; the Excel file needs none, because its text cells
 * are never formulas (ADR 9010).
 */
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

export function neutraliseFormula(value: string): string {
  return FORMULA_TRIGGER.test(value) ? `'${value}` : value;
}

/**
 * A cell's raw serialisation. Full precision: `String(number)` is the shortest
 * round-trippable representation in JavaScript, so no precision is lost, and
 * nothing is rounded on the way out. In the decimal-comma dialect the same digits with the point
 * a comma; an integer, a date, a code and text are written alike in both.
 */
export function serialiseCell(cell: ExportCell, dialect: CsvDialect = 'standard'): string {
  switch (cell.kind) {
    case 'number':
    case 'money':
    case 'percent':
      return decimal(cell.raw, dialect);
    case 'integer':
      return String(Math.trunc(cell.raw));
    case 'date':
      return cell.iso;
    case 'text':
      return neutraliseFormula(cell.value);
    case 'label':
      return neutraliseFormula(cell.value);
    case 'missing':
      // reqs/00 D8 — the three states are distinguishable in the file. An empty
      // field would collapse `not-ranged` and `not-reported` into each other, and a
      // zero would collapse both into `counted-zero`, which is the exact confusion
      // G2.5 forbids at the pixel and G2.4 must not reintroduce in the data.
      return cell.state;
    default: {
      const exhaustive: never = cell;
      throw new Error(`unhandled cell kind: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/** The header row is the shared builder's (`table.ts`), restated so existing imports keep working. */
export { headerFields };

/** A row's fields: the shared builder's cells (`table.ts`), each written as CSV text. */
function rowFields(columns: readonly ExportColumn[], row: readonly ExportCell[], dialect: CsvDialect): string[] {
  return fileRow(columns, row).map((field) => (field.kind === 'currency' ? field.currency : serialiseCell(field.cell, dialect)));
}

/** One line's fields, escaped and joined with the dialect's separator (no line break). */
function line(fields: readonly string[], dialect: CsvDialect): string {
  return fields.map((field) => escapeField(field, dialect)).join(CSV_SEPARATOR[dialect]);
}

/** Streams CSV text. Async so a >100k-row extract never has to be resident. */
export async function* streamCsv(request: ExportRequest, dialect: CsvDialect = 'standard'): AsyncGenerator<string> {
  yield UTF8_BOM + line(headerFields(request.columns), dialect) + CRLF;
  for await (const row of request.rows() as AsyncIterable<readonly ExportCell[]>) {
    yield line(rowFields(request.columns, row, dialect), dialect) + CRLF;
  }
}

/**
 * About how many characters of CSV text are gathered before they are handed to a `Blob`. A Blob's bytes
 * live outside the page's JavaScript heap (the browser keeps them, and pages a large one to disk), so
 * the text the file is written from never accumulates as strings: at most one batch at a time.
 */
export const CSV_BLOB_BATCH_CHARS = 1 << 20;

/** How often, in rows, `toCsvBlob` says how many rows it has written (`docs/adr/9074`). */
export const CSV_PROGRESS_EVERY = 2000;

/**
 * The file as a Blob. `onProgress` hears the rows written so far, every {@link CSV_PROGRESS_EVERY} and once at the end — the
 * header is not a row (`docs/adr/9074`).
 */
export async function toCsvBlob(request: ExportRequest, dialect: CsvDialect = 'standard', onProgress?: (rows: number) => void): Promise<Blob> {
  // The file as a list of Blobs of about a megabyte each, never as one list of every line: a run's cells
  // file of 504,246 rows (2026-10-23) held its whole text as strings, beside its rows, and ran the tab
  // out of memory.
  const blobs: Blob[] = [];
  let batch: string[] = [];
  let size = 0;
  // The first chunk is the header; each one after it is a row.
  let rows = -1;
  for await (const chunk of streamCsv(request, dialect)) {
    batch.push(chunk);
    size += chunk.length;
    rows += 1;
    if (rows > 0 && rows % CSV_PROGRESS_EVERY === 0) onProgress?.(rows);
    if (size >= CSV_BLOB_BATCH_CHARS) {
      blobs.push(new Blob([batch.join('')]));
      batch = [];
      size = 0;
    }
  }
  if (batch.length > 0) blobs.push(new Blob([batch.join('')]));
  onProgress?.(Math.max(0, rows));
  // `text/csv;charset=utf-8` plus the BOM: belt and braces, because Excel trusts
  // the BOM and almost nothing else.
  return new Blob(blobs, { type: 'text/csv;charset=utf-8' });
}

/** Synchronous variant, for tests and for the ≤100k path with resident rows. */
export function toCsvString(request: ExportRequest, dialect: CsvDialect = 'standard'): string {
  const rows = request.rows() as Iterable<readonly ExportCell[]>;
  const out = [UTF8_BOM + line(headerFields(request.columns), dialect)];
  for (const row of rows) out.push(line(rowFields(request.columns, row, dialect), dialect));
  return out.join(CRLF) + CRLF;
}
